import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import InventoryService from "../../../api/inventory";

const RecipeModal = ({ open, onClose, onSave, product, showAlert }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [ingredients, setIngredients] = useState([]);
  const [recipeItems, setRecipeItems] = useState([]);
  const [preparationMethod, setPreparationMethod] = useState("");
  const [yieldQuantity, setYieldQuantity] = useState(1);
  const [preparationTime, setPreparationTime] = useState("");
  const [loading, setLoading] = useState(false);

  // New item form
  const [newIngredient, setNewIngredient] = useState(null);
  const [newGrossQty, setNewGrossQty] = useState("");
  const [newFC, setNewFC] = useState("1.0");
  const [newUnit, setNewUnit] = useState("g");
  const [creatingIngredient, setCreatingIngredient] = useState(false);

  useEffect(() => {
    if (open && product) {
      loadData();
    }
  }, [open, product]);

  const loadData = async () => {
    try {
      const [ingredientsRes, recipeRes] = await Promise.all([
        InventoryService.getIngredients({ page_size: 500 }),
        InventoryService.getProductRecipe(product.product_id),
      ]);

      setIngredients(ingredientsRes.data.results || []);

      const recipe = recipeRes.data;
      setRecipeItems(recipe.items || []);
      setPreparationMethod(recipe.preparation_method || "");
      setYieldQuantity(recipe.yield_quantity || 1);
      setPreparationTime(recipe.preparation_time || "");
    } catch (err) {
      console.error("Error loading recipe:", err);
    }
  };

  const handleAddItem = async () => {
    if (!newIngredient || !newGrossQty) return;

    let ingredientToAdd = newIngredient;

    // If user typed a free text name (not an existing ingredient), create it first
    if (typeof newIngredient === "string") {
      setCreatingIngredient(true);
      try {
        const res = await InventoryService.createIngredient({
          name: newIngredient.trim(),
          unit: newUnit,
          category: "outros",
          current_cost: 0,
          minimum_stock: 0,
        });
        ingredientToAdd = res.data;
        setIngredients((prev) => [...prev, ingredientToAdd]);
        showAlert(`Ingrediente "${ingredientToAdd.name}" criado automaticamente!`);
      } catch (err) {
        const msg =
          err.response?.data?.error ||
          err.response?.data?.detail ||
          "Erro ao criar ingrediente";
        showAlert(msg, "error");
        setCreatingIngredient(false);
        return;
      } finally {
        setCreatingIngredient(false);
      }
    }

    const alreadyExists = recipeItems.some(
      (item) => (item.ingredient || item.ingredient_id) === ingredientToAdd.id
    );
    if (alreadyExists) {
      showAlert("Este ingrediente ja esta na receita.", "warning");
      return;
    }

    setRecipeItems([
      ...recipeItems,
      {
        ingredient: ingredientToAdd.id,
        ingredient_name: ingredientToAdd.name,
        ingredient_unit: ingredientToAdd.unit,
        ingredient_current_cost: ingredientToAdd.current_cost,
        gross_quantity: newGrossQty,
        correction_factor: newFC || 1.0,
        unit: newUnit,
      },
    ]);

    setNewIngredient(null);
    setNewGrossQty("");
    setNewFC("1.0");
    setNewUnit("g");
  };

  const handleRemoveItem = (index) => {
    setRecipeItems(recipeItems.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await InventoryService.updateProductRecipe(product.product_id, {
        preparation_method: preparationMethod,
        yield_quantity: yieldQuantity || 1,
        preparation_time: preparationTime || null,
        items: recipeItems.map((item) => ({
          ingredient: item.ingredient || item.ingredient_id,
          gross_quantity: item.gross_quantity,
          correction_factor: item.correction_factor || 1.0,
          unit: item.unit || "g",
        })),
      });
      onSave();
    } catch (err) {
      const msg = err.response?.data?.error || "Erro ao salvar ficha tecnica";
      showAlert(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const calcNetQty = (gross, fc) => {
    const g = Number(gross) || 0;
    const f = Number(fc) || 1;
    return f > 0 ? g / f : g;
  };

  const calcItemCost = (gross, fc, cost) => {
    const net = calcNetQty(gross, fc);
    return net * (Number(cost) || 0);
  };

  const totalCost = recipeItems.reduce(
    (sum, item) =>
      sum +
      calcItemCost(
        item.gross_quantity,
        item.correction_factor,
        item.ingredient_current_cost
      ),
    0
  );

  const costPerPortion = yieldQuantity > 0 ? totalCost / yieldQuantity : totalCost;
  const sellingPrice = Number(product?.price) || 0;
  const cmv = sellingPrice > 0 ? (costPerPortion / sellingPrice) * 100 : 0;

  const formatMoney = (val) =>
    Number(val || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        Ficha Tecnica — {product?.title}
      </DialogTitle>
      <DialogContent dividers>
        {/* Product info & costs */}
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Paper
              variant="outlined"
              sx={{ p: 1.5, borderRadius: 2, textAlign: "center", height: "100%" }}
            >
              <Typography variant="caption" color="text.secondary">
                Custo Total
              </Typography>
              <Typography variant="h6" fontWeight={700} color="primary">
                {formatMoney(totalCost)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper
              variant="outlined"
              sx={{ p: 1.5, borderRadius: 2, textAlign: "center", height: "100%" }}
            >
              <Typography variant="caption" color="text.secondary">
                Custo por Porcao
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {formatMoney(costPerPortion)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper
              variant="outlined"
              sx={{ p: 1.5, borderRadius: 2, textAlign: "center", height: "100%" }}
            >
              <Typography variant="caption" color="text.secondary">
                Preco de Venda
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {formatMoney(sellingPrice)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                textAlign: "center",
                height: "100%",
                borderColor: cmv > 35 ? "error.main" : cmv > 25 ? "warning.main" : "success.main",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                CMV %
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                color={cmv > 35 ? "error.main" : cmv > 25 ? "warning.main" : "success.main"}
              >
                {cmv.toFixed(1)}%
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Yield & time */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Rendimento (porcoes)"
              type="number"
              value={yieldQuantity}
              onChange={(e) => setYieldQuantity(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ min: 1, step: 1 }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Tempo de preparo (min)"
              type="number"
              value={preparationTime}
              onChange={(e) => setPreparationTime(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>

        {/* Recipe items table */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Ingredientes da Receita
        </Typography>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderRadius: 2, mb: 2 }}
        >
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: alpha("#2563eb", 0.04) }}>
                <TableCell>Ingrediente</TableCell>
                <TableCell align="right">Peso Bruto (PB)</TableCell>
                <TableCell align="right">FC</TableCell>
                <TableCell align="right">Peso Liquido (PL)</TableCell>
                <TableCell align="right">Custo</TableCell>
                <TableCell align="center" width={50}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recipeItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary" variant="body2">
                      Nenhum ingrediente adicionado.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                recipeItems.map((item, idx) => {
                  const netQty = calcNetQty(item.gross_quantity, item.correction_factor);
                  const itemCost = calcItemCost(
                    item.gross_quantity,
                    item.correction_factor,
                    item.ingredient_current_cost
                  );
                  return (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {item.ingredient_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {Number(item.gross_quantity).toFixed(3)} {item.unit}
                      </TableCell>
                      <TableCell align="right">
                        {Number(item.correction_factor).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {netQty.toFixed(3)} {item.unit}
                      </TableCell>
                      <TableCell align="right">{formatMoney(itemCost)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveItem(idx)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add ingredient form */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Adicionar Ingrediente
          </Typography>
          <Grid container spacing={1.5} alignItems="flex-start">
            <Grid item xs={12} sm={4}>
              <Autocomplete
                freeSolo
                options={ingredients}
                getOptionLabel={(opt) =>
                  typeof opt === "string" ? opt : `${opt.name} (${opt.unit})`
                }
                value={newIngredient}
                onChange={(_, val) => {
                  if (val && val._isNew) {
                    setNewIngredient(val.name);
                  } else {
                    setNewIngredient(val);
                    if (val && typeof val !== "string") setNewUnit(val.unit);
                  }
                }}
                onInputChange={(_, val, reason) => {
                  if (reason === "input") {
                    const match = ingredients.find(
                      (i) => `${i.name} (${i.unit})` === val
                    );
                    if (!match && val.trim()) {
                      setNewIngredient(val.trim());
                    }
                  }
                }}
                filterOptions={(options, params) => {
                  const filtered = options.filter((opt) =>
                    opt.name
                      .toLowerCase()
                      .includes(params.inputValue.toLowerCase())
                  );
                  if (
                    params.inputValue !== "" &&
                    !filtered.some(
                      (o) =>
                        o.name.toLowerCase() ===
                        params.inputValue.toLowerCase()
                    )
                  ) {
                    filtered.push({
                      _isNew: true,
                      name: params.inputValue,
                      unit: newUnit,
                      current_cost: 0,
                    });
                  }
                  return filtered;
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    {option._isNew ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AddIcon sx={{ fontSize: 16, color: "#2563eb" }} />
                        <Typography variant="body2">
                          Criar "{option.name}"
                        </Typography>
                      </Stack>
                    ) : (
                      `${option.name} (${option.unit})`
                    )}
                  </li>
                )}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ingrediente"
                    helperText="Selecione ou digite para criar novo"
                  />
                )}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                label="PB (qtd)"
                type="number"
                value={newGrossQty}
                onChange={(e) => setNewGrossQty(e.target.value)}
                fullWidth
                size="small"
                inputProps={{ min: 0, step: 0.001 }}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                label="FC"
                type="number"
                value={newFC}
                onChange={(e) => setNewFC(e.target.value)}
                fullWidth
                size="small"
                inputProps={{ min: 0.01, step: 0.01 }}
                helperText="1.0 = sem perda"
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                select
                label="Unidade"
                value={newUnit}
                onChange={(e) => setNewUnit(e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="kg">kg</MenuItem>
                <MenuItem value="g">g</MenuItem>
                <MenuItem value="L">L</MenuItem>
                <MenuItem value="ml">ml</MenuItem>
                <MenuItem value="un">un</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                disabled={!newIngredient || !newGrossQty || creatingIngredient}
                fullWidth
                sx={{ height: 40, textTransform: "none" }}
              >
                {creatingIngredient ? "Criando..." : "Adicionar"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Preparation method */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Modo de Preparo
        </Typography>
        <TextField
          value={preparationMethod}
          onChange={(e) => setPreparationMethod(e.target.value)}
          fullWidth
          multiline
          rows={4}
          placeholder="Descreva o passo a passo do preparo..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          sx={{
            backgroundColor: "#2563eb",
            "&:hover": { backgroundColor: "#1d4ed8" },
          }}
        >
          {loading ? "Salvando..." : "Salvar Ficha Tecnica"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipeModal;
